import { useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { useAuthStore } from './stores/authStore'
import { useThemeStore } from './stores/themeStore'
import { setUnauthorizedHandler } from './services/apiService'
import { queryClient } from './lib/queryClient'
import { Toaster } from './components/ui/toaster'
import { TooltipProvider } from './components/ui/tooltip'
import { useToast } from './hooks/use-toast'
import { useAuth, initializeTokenGetter } from './hooks/use-auth'
import { usePermissionsLoader } from './hooks/use-permissions'
import { usePermissionsStore } from './stores/permissionsStore'
import { sessionStorage } from './services/api/sessionStorage'
import Login from './pages/Login'
import Dashboard from './pages/Dashboard'
import PoolDetailPage from './pages/PoolDetailPage'
import ControlPage from './pages/ControlPage'
import Settings from './pages/Settings'
import Accounts from './pages/Accounts'
import AccountDetailPage from './pages/AccountDetailPage'
import SimulatorPage from './pages/SimulatorPage'
import SystemsPage from './pages/SystemsPage'
import DocsPage from './pages/DocsPage'
import Layout from './components/layout/Layout'

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const auth = useAuth()

  // Show loading state while Auth0 is checking authentication
  if (auth.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  if (!auth.isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <Layout>{children}</Layout>
}

/**
 * Inner component that sets up global error handling for unauthorized errors
 * Must be inside BrowserRouter to use useNavigate
 */
function AppContent() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const auth = useAuth()

  // Load permissions after authentication
  usePermissionsLoader()

  // Initialize token getter for HTTP client
  useEffect(() => {
    initializeTokenGetter(auth.getToken)
  }, [auth.getToken])

  // Reset permissions when the user logs out
  useEffect(() => {
    if (!auth.isAuthenticated) {
      usePermissionsStore.getState().reset()
      queryClient.removeQueries({ queryKey: ['user-policies'] })
    }
  }, [auth.isAuthenticated])

  // Set up unauthorized handler on mount
  useEffect(() => {
    setUnauthorizedHandler(() => {
      console.log('[App] Unauthorized error detected - logging out user')

      // Clear all cached data
      queryClient.clear()
      usePermissionsStore.getState().reset()

      // Log out user (auth.logout() clears Auth0's isAuthenticated for Auth0 mode,
      // so the /login route no longer redirects back to /)
      auth.logout()

      // Show notification
      toast({
        variant: 'destructive',
        title: 'Session Expired',
        description: 'Your session has expired. Please log in again.',
      })

      // Navigate to login (no-op for Auth0 mode since auth0Logout redirects the browser)
      navigate('/login', { replace: true })
    })
  }, [auth.logout, navigate, toast])

  return (
    <>
      <Routes>
        <Route
          path="/login"
          element={auth.isAuthenticated ? <Navigate to="/" replace /> : <Login />}
        />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route path="/pools" element={<Navigate to="/" replace />} />
        <Route
          path="/pools/:poolID/:tab?"
          element={
            <ProtectedRoute>
              <PoolDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/control"
          element={
            <ProtectedRoute>
              <ControlPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/accounts"
          element={
            <ProtectedRoute>
              <Accounts />
            </ProtectedRoute>
          }
        />
        <Route
          path="/accounts/:accountID"
          element={
            <ProtectedRoute>
              <AccountDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/simulator"
          element={
            <ProtectedRoute>
              <SimulatorPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/systems/:systemID?/:tab?"
          element={
            <ProtectedRoute>
              <SystemsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/docs"
          element={
            <ProtectedRoute>
              <DocsPage />
            </ProtectedRoute>
          }
        />
      </Routes>
      <Toaster />
    </>
  )
}

function App() {
  const checkAuth = useAuthStore((state) => state.checkAuth)
  const theme = useThemeStore((state) => state.theme)

  // Apply theme class to document root
  useEffect(() => {
    const root = document.documentElement

    // Helper function to apply theme based on system preference
    const applySystemTheme = () => {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
      root.classList.remove('dark', 'matrix', 'midnight')
      if (prefersDark) {
        root.classList.add('dark')
      }
    }

    // Remove all theme classes first
    root.classList.remove('dark', 'matrix', 'midnight')

    if (theme === 'system') {
      // Apply system preference
      applySystemTheme()

      // Listen for system theme changes
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
      const handleChange = () => applySystemTheme()

      // Modern browsers support addEventListener
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', handleChange)
      } else {
        // Fallback for older browsers
        mediaQuery.addListener(handleChange)
      }

      return () => {
        if (mediaQuery.removeEventListener) {
          mediaQuery.removeEventListener('change', handleChange)
        } else {
          mediaQuery.removeListener(handleChange)
        }
      }
    } else if (theme === 'dark') {
      root.classList.add('dark')
    } else if (theme === 'midnight') {
      root.classList.add('midnight')
    } else if (theme === 'matrix') {
      root.classList.add('matrix')
    }
    // light theme = no class
  }, [theme])

  useEffect(() => {
    // Check if there's an API key in session storage
    // If so, validate the session by calling checkAuth
    // Auth0 mode handles authentication state automatically via the Auth0Provider
    const session = sessionStorage.get()
    if (session?.apiKey) {
      checkAuth()
    }
  }, [checkAuth])

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider delayDuration={300}>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
        <ReactQueryDevtools initialIsOpen={false} />
      </TooltipProvider>
    </QueryClientProvider>
  )
}

export default App
